import { Dialog } from '@syncfusion/ej2-popups';

import { IndividualRelationshipType } from '@/shared/types/enum';
import { formatEnum } from '@/shared/utils/format';
import { escapeHtml } from '@/shared/utils/misc';

export type CompaniesHousePersonDetails = {
  dateOfBirth: string;
  givenName: string;
  name: string;
  role: string;
  surname: string;
};

export type CompaniesHousePersonMatch = {
  alreadyLinked: boolean;
  birthDate: string;
  id: string;
  name: string;
  relationshipType: string;
};

export type CompaniesHousePersonModalResult = { action: 'create' } | { action: 'link'; individualId: string } | null;

let dialog: Dialog | null = null;
let resolvePromise: ((value: CompaniesHousePersonModalResult) => void) | null = null;

const settle = (value: CompaniesHousePersonModalResult) => {
  resolvePromise?.(value);
  resolvePromise = null;
};

const MATCH_RADIO_NAME = 'companiesHousePersonMatch';

const buildContent = (person: CompaniesHousePersonDetails, matches: CompaniesHousePersonMatch[]) => {
  const firstLinkableIndex = matches.findIndex((match) => !match.alreadyLinked);
  const allLinked = matches.every((match) => match.alreadyLinked);

  return `
  <div class="d-flex flex-column gap-3">
    <div class="alert alert-warning mb-0 small" role="alert">
      Companies House lists <strong>${escapeHtml(person.name)}</strong> as ${escapeHtml(person.role)}${
        person.dateOfBirth ? `, born ${escapeHtml(person.dateOfBirth)}` : ''
      }. PracticeFlow already has ${matches.length === 1 ? 'a possible matching individual' : 'possible matching individuals'}${
        allLinked ? ' linked to this organisation' : ''
      }.
    </div>
    <div class="d-flex flex-column gap-2">
      ${matches
        .map(
          (match, index) => `
        <div class="form-check mb-0">
          <input class="form-check-input" type="radio" name="${MATCH_RADIO_NAME}" id="${MATCH_RADIO_NAME}${index}" value="${escapeHtml(match.id)}"${
            match.alreadyLinked ? ' disabled' : ''
          }${index === firstLinkableIndex ? ' checked' : ''} />
          <label class="form-check-label" for="${MATCH_RADIO_NAME}${index}">
            ${escapeHtml(match.name)}
            <span class="text-muted fs-7">
              ${escapeHtml(formatEnum(IndividualRelationshipType, match.relationshipType))}${match.birthDate ? ` &middot; born ${escapeHtml(match.birthDate)}` : ''}${
                match.alreadyLinked ? ' &middot; Already linked' : ''
              }
            </span>
          </label>
        </div>`,
        )
        .join('')}
    </div>
  </div>
`;
};

const setLinkEnabled = (enabled: boolean) => {
  const button = document.querySelector<HTMLButtonElement>('#companiesHousePersonDialog button.e-primary');

  if (!button) return;

  button.disabled = !enabled;
};

const selectedIndividualId = () =>
  document.querySelector<HTMLInputElement>(`#companiesHousePersonDialog input[name="${MATCH_RADIO_NAME}"]:checked`)
    ?.value;

const ensureDialog = () => {
  if (dialog) {
    return dialog;
  }

  const host = document.createElement('div');
  host.id = 'companiesHousePersonDialog';
  document.body.appendChild(host);

  dialog = new Dialog({
    header: 'A matching Individual may already exist',
    content: '',
    target: document.body,
    showCloseIcon: true,
    isModal: true,
    visible: false,
    width: '560px',
    zIndex: 1035,
    animationSettings: { effect: 'Fade' },
    buttons: [
      {
        click: () => {
          dialog?.hide();
          settle(null);
        },
        buttonModel: { content: 'Cancel', cssClass: 'e-btn e-flat' },
      },
      {
        click: () => {
          settle({ action: 'create' });
          dialog?.hide();
        },
        buttonModel: { content: 'Create new anyway', cssClass: 'e-btn e-flat' },
      },
      {
        click: () => {
          const individualId = selectedIndividualId();
          if (!individualId) return;

          settle({ action: 'link', individualId });
          dialog?.hide();
        },
        buttonModel: { content: 'Link selected', cssClass: 'e-btn e-primary', isPrimary: true },
      },
    ],
    close: () => settle(null),
  });

  dialog.appendTo(host);
  return dialog;
};

export const openCompaniesHousePersonModal = (
  person: CompaniesHousePersonDetails,
  matches: CompaniesHousePersonMatch[],
): Promise<CompaniesHousePersonModalResult> =>
  new Promise((resolve) => {
    resolvePromise = resolve;

    const dlg = ensureDialog();

    dlg.content = buildContent(person, matches);
    dlg.dataBind();
    dlg.show();
    setLinkEnabled(matches.some((match) => !match.alreadyLinked));
  });
